import React, { useEffect, useRef, useState } from 'react';
import { Camera, Upload } from 'lucide-react';
import { bestDescriptor, fileToImage, frameToJpeg, imageToJpeg } from '../faceKit';
import { useStreamVideo } from '../media';

async function analyse(canvas, kitReady, size) {
  if (!kitReady) return { faces: null, descriptor: null };
  try {
    const r = await bestDescriptor(canvas, size);
    return { faces: r.count, descriptor: r.descriptor };
  } catch (e) {
    console.error('face analyse failed', e);
    return { faces: null, descriptor: null };
  }
}

const usePreview = (shot) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!shot?.blob) return undefined;
    const u = URL.createObjectURL(shot.blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [shot]);
  return url;
};

const ShotPreview = ({ shot, alt, testId }) => {
  const url = usePreview(shot);
  return <div className="iv-video">{url && <img src={url} alt={alt} data-testid={testId} />}</div>;
};

export function IdCapture({ stream, kitReady, onCaptured }) {
  const [, setVideo] = useStreamVideo(stream);
  const videoEl = useRef(null);
  const [shot, setShot] = useState(null);
  const [busy, setBusy] = useState(false);
  const bind = (el) => { videoEl.current = el; setVideo(el); };

  const finish = async ({ blob, canvas }) => setShot({ blob, ...(await analyse(canvas, kitReady, 608)) });
  const capture = async () => {
    setBusy(true);
    try { await finish(await frameToJpeg(videoEl.current, 1280, 0.88)); } finally { setBusy(false); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { await finish(await imageToJpeg(await fileToImage(f), 1280)); } finally { setBusy(false); e.target.value = ''; }
  };

  if (shot) {
    const noFace = kitReady && !shot.faces;
    return (
      <div data-testid="id-capture-review">
        <ShotPreview shot={shot} alt="Your ID photo" testId="id-photo-preview" />
        <div className={`iv-note ${noFace ? 'is-warn' : 'is-good'}`} style={{ marginTop: '0.75rem' }} data-testid="id-face-status">
          {!kitReady ? 'Photo captured. A person will compare it with your selfie.' : noFace
            ? 'We could not find the photo on your ID. Try better lighting, hold it closer and avoid glare.'
            : 'Face found on your ID.'}
        </div>
        <div className="iv-row-end">
          <button type="button" className="iv-btn iv-btn-ghost" onClick={() => setShot(null)} data-testid="id-retake-btn">Retake</button>
          <button type="button" className="iv-btn iv-btn-primary" onClick={() => onCaptured(shot)} data-testid="id-use-photo-btn">Use this photo</button>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="id-capture">
      <div className="iv-video">
        <video ref={bind} autoPlay muted playsInline data-testid="id-camera-preview" />
        <div className="iv-idframe" />
        <div className="iv-prompt">Hold your photo ID inside the frame</div>
      </div>
      <p className="iv-small" style={{ marginTop: '0.6rem' }}>Any government photo ID works (passport, driving licence, national ID). Only the photo is compared; we don't read your ID number.</p>
      <div className="iv-row-end">
        <label className="iv-btn iv-btn-ghost" data-testid="id-upload-label">
          <Upload size={16} /> Upload a photo
          <input type="file" accept="image/*" capture="environment" hidden onChange={upload} data-testid="id-upload-input" />
        </label>
        <button type="button" className="iv-btn iv-btn-primary" disabled={busy} onClick={capture} data-testid="id-capture-btn">
          <Camera size={16} /> {busy ? 'Checking…' : 'Capture ID'}
        </button>
      </div>
    </div>
  );
}

export function SelfieCapture({ stream, kitReady, onCaptured }) {
  const [, setVideo] = useStreamVideo(stream);
  const videoEl = useRef(null);
  const [shot, setShot] = useState(null);
  const [busy, setBusy] = useState(false);
  const bind = (el) => { videoEl.current = el; setVideo(el); };

  const capture = async () => {
    setBusy(true);
    try {
      const { blob, canvas } = await frameToJpeg(videoEl.current, 640, 0.88);
      setShot({ blob, ...(await analyse(canvas, kitReady, 416)) });
    } finally {
      setBusy(false);
    }
  };

  if (shot) {
    const bad = kitReady && shot.faces !== 1;
    return (
      <div data-testid="selfie-review">
        <ShotPreview shot={shot} alt="Your selfie" testId="selfie-preview" />
        <div className={`iv-note ${bad ? 'is-warn' : 'is-good'}`} style={{ marginTop: '0.75rem' }} data-testid="selfie-face-status">
          {!kitReady ? 'Selfie captured.' : shot.faces === 0 ? 'We could not see your face. Face the camera in good light.'
            : shot.faces > 1 ? 'Only you should be in view. Please retake.' : 'Looks good.'}
        </div>
        <div className="iv-row-end">
          <button type="button" className="iv-btn iv-btn-ghost" onClick={() => setShot(null)} data-testid="selfie-retake-btn">Retake</button>
          <button type="button" className="iv-btn iv-btn-primary" disabled={bad} onClick={() => onCaptured(shot)} data-testid="selfie-use-btn">Continue</button>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="selfie-capture">
      <div className="iv-video">
        <video ref={bind} className="is-mirror" autoPlay muted playsInline data-testid="selfie-camera-preview" />
        <div className="iv-oval" />
        <div className="iv-prompt">Centre your face in the oval</div>
      </div>
      <div className="iv-row-end">
        <button type="button" className="iv-btn iv-btn-primary" disabled={busy} onClick={capture} data-testid="selfie-capture-btn">
          <Camera size={16} /> {busy ? 'Checking…' : 'Take selfie'}
        </button>
      </div>
    </div>
  );
}
